import type { PlanEntitlement } from '@/types/entitlements';
import { FREE_PLAN_LIMITS } from '@/types/entitlements';
import type { Trip } from '@/services/trips';

export function deriveEntitlement(input: {
  isPremium?: boolean;
  planTier?: 'free' | 'premium';
  entitlement?: PlanEntitlement;
}): PlanEntitlement {
  const hasPremiumSignal =
    input.planTier === 'premium' ||
    Boolean(input.isPremium) ||
    input.entitlement?.status === 'active' ||
    input.entitlement?.status === 'grace_period' ||
    input.entitlement?.status === 'cancelled';

  if (input.entitlement) {
    // Keep webhook payload as source of truth, but reconcile known migration/race
    // states where `isPremium` is true while `entitlement.tier` still says free.
    if (hasPremiumSignal && input.entitlement.tier !== 'premium') {
      return {
        ...input.entitlement,
        tier: 'premium',
      };
    }
    return input.entitlement;
  }

  const tier: 'free' | 'premium' =
    hasPremiumSignal ? 'premium' : 'free';

  return {
    tier,
    status: tier === 'premium' ? 'active' : 'inactive',
    provider: 'manual',
  };
}

export function hasPremiumAccess(input: {
  isPremium?: boolean;
  planTier?: 'free' | 'premium';
  entitlement?: PlanEntitlement;
}): boolean {
  const entitlement = deriveEntitlement(input);
  return entitlement.tier === 'premium' && entitlement.status !== 'expired';
}

export function canCreateAnotherActiveTrip(input: {
  isPremium?: boolean;
  planTier?: 'free' | 'premium';
  entitlement?: PlanEntitlement;
  activeTripCount: number;
}): boolean {
  if (hasPremiumAccess(input)) {
    return true;
  }
  return input.activeTripCount < FREE_PLAN_LIMITS.maxActiveTrips;
}

type TripLimitComparable = Pick<Trip, 'owner' | 'members' | 'memberIds' | 'isArchived' | 'endDate'>;

function isOwnedByUserForFreeLimit(trip: TripLimitComparable, ownerId: string): boolean {
  if (!ownerId) {
    return false;
  }
  const owner = trip.owner;
  const legacyOwnerId = (trip as unknown as { ownerId?: unknown }).ownerId;
  const legacyCreatedBy = (trip as unknown as { createdBy?: unknown }).createdBy;

  if (owner === ownerId || legacyOwnerId === ownerId || legacyCreatedBy === ownerId) {
    return true;
  }
  // Count active shared trips toward the free-plan cap (align with server `getActiveTripCount`).
  if (Array.isArray(trip.members) && trip.members.includes(ownerId)) {
    return true;
  }
  if (Array.isArray(trip.memberIds) && trip.memberIds.includes(ownerId)) {
    return true;
  }
  return false;
}

const normalizeToStartOfDay = (date: Date): Date => {
  const normalized = new Date(date);
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

/**
 * Mirrors Cloud Function entitlement gating:
 * - archived trips do not count
 * - trips ending before today do not count
 * - invalid/missing endDate is treated as active to avoid under-enforcing
 */
const INVALID_TRIP_DATE_YEAR_CUTOFF = 1971;

function isLikelyInvalidTripLimitDate(date: Date): boolean {
  return !Number.isFinite(date.getTime()) || date.getFullYear() <= INVALID_TRIP_DATE_YEAR_CUTOFF;
}

export function isTripCountedAsActiveForFreeLimit(
  trip: TripLimitComparable,
  now: Date = new Date(),
): boolean {
  if (trip.isArchived === true) {
    return false;
  }
  const endRaw = trip.endDate;
  const endDate =
    endRaw instanceof Date
      ? endRaw
      : typeof endRaw === 'string' || typeof endRaw === 'number'
        ? new Date(endRaw)
        : null;
  // Match server entitlement logic: missing/invalid dates count as active (avoid under-enforcing).
  if (!endDate || !Number.isFinite(endDate.getTime()) || isLikelyInvalidTripLimitDate(endDate)) {
    return true;
  }
  return normalizeToStartOfDay(endDate) >= normalizeToStartOfDay(now);
}

export function countOwnedActiveTripsForFreeLimit(
  trips: TripLimitComparable[],
  ownerId: string,
  now: Date = new Date(),
): number {
  if (!ownerId) {
    return 0;
  }
  return trips.filter((trip) => isOwnedByUserForFreeLimit(trip, ownerId) && isTripCountedAsActiveForFreeLimit(trip, now)).length;
}

/** Thrown after presenting the premium gate so async callers (e.g. modals) do not treat the flow as success. */
export class PremiumGatePresentedError extends Error {
  readonly name = 'PremiumGatePresentedError';
  constructor(message = 'Premium gate presented') {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** `instanceof` can fail across bundles/Hermes; use this in UI catch blocks. */
export function isPremiumGatePresentedError(error: unknown): error is PremiumGatePresentedError {
  if (error instanceof PremiumGatePresentedError) {
    return true;
  }
  return typeof error === 'object' && error !== null && (error as { name?: string }).name === 'PremiumGatePresentedError';
}

/** Duplicate template create submit — modal should stay open; do not dismiss. */
export const TEMPLATE_CREATE_IN_PROGRESS_REASON = 'template_create_in_progress' as const;

const TRIP_LIMIT_ENTITLEMENT_MESSAGE_FRAGMENT = 'free plan allows 1 active trip';

export function isTripLimitEntitlementError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const code = String((error as { code?: unknown }).code ?? '').toLowerCase();
  const message = String((error as { message?: unknown }).message ?? '').toLowerCase();
  const details = String((error as { details?: unknown }).details ?? '').toLowerCase();
  const haystack = `${message} ${details}`;
  if (
    code.includes('failed-precondition') &&
    (haystack.includes(TRIP_LIMIT_ENTITLEMENT_MESSAGE_FRAGMENT) ||
      /\b1 active trip\b/.test(haystack) && /\bfree\b/.test(haystack) && /\bupgrade\b/.test(haystack))
  ) {
    return true;
  }
  return haystack.includes(TRIP_LIMIT_ENTITLEMENT_MESSAGE_FRAGMENT);
}
